import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CartDrawer() {
  const { items, updateQuantity, removeItem, total, itemCount, isOpen, setIsOpen } = useCart();
  const { t } = useLanguage();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full sm:max-w-md bg-[hsl(36,33%,98%)] border-l border-[hsl(32,17%,84%)] flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-[family-name:var(--font-display)] text-xl flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            {t('cart')} ({itemCount})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <ShoppingBag className="w-12 h-12 mx-auto text-[hsl(32,17%,84%)] mb-3" />
              <p className="text-[hsl(22,10%,38%)]">{t('cart_empty')}</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-3 py-4">
              {items.map(item => (
                <div key={item.id} data-testid="cart-item-row" className="flex items-center gap-3 p-3 bg-white rounded-xl border border-[hsl(32,17%,84%)]">
                  {item.image && (
                    <img src={item.image} alt={item.name} className="w-14 h-14 rounded-lg object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    <p className="text-sm text-[hsl(16,55%,36%)] font-semibold">{(item.price * item.quantity).toFixed(2)} €</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid="cart-quantity-decrease"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-[hsl(32,17%,84%)] hover:bg-[hsl(35,20%,92%)] transition-colors duration-150"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                    <button
                      data-testid="cart-quantity-increase"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-[hsl(32,17%,84%)] hover:bg-[hsl(35,20%,92%)] transition-colors duration-150"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                    <button
                      data-testid="cart-remove-item"
                      onClick={() => removeItem(item.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-[hsl(0,72%,45%)] hover:bg-red-50 transition-colors duration-150 ml-1"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[hsl(32,17%,84%)] pt-4 space-y-3">
              <div className="flex items-center justify-between text-lg font-semibold">
                <span>{t('total')}</span>
                <span className="text-[hsl(16,55%,36%)]">{total.toFixed(2)} €</span>
              </div>
              <Link
                to="/order?checkout=true"
                data-testid="cart-checkout-button"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center justify-center px-4 py-3 rounded-xl text-sm font-medium bg-[hsl(16,55%,36%)] text-[hsl(38,44%,96%)] shadow-[0_10px_24px_rgba(33,24,16,.10)] hover:brightness-[1.03] transition-colors duration-200"
              >
                {t('checkout')} — {total.toFixed(2)} €
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
